import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Users, MousePointer, Award } from 'lucide-react';
// Sample data for charts
const engagementTimelineData = [{
  time: '6AM',
  engagement: 120
}, {
  time: '9AM',
  engagement: 280
}, {
  time: '12PM',
  engagement: 200
}, {
  time: '3PM',
  engagement: 180
}, {
  time: '6PM',
  engagement: 250
}, {
  time: '9PM',
  engagement: 190
}];
const platformComparisonData = [{
  name: 'Facebook',
  value: 68
}, {
  name: 'Twitter',
  value: 32
}];
const demographicsData = [{
  name: '18-24',
  value: 15
}, {
  name: '25-34',
  value: 60
}, {
  name: '35-44',
  value: 20
}, {
  name: '45+',
  value: 5
}];
const postTypesData = [{
  name: 'Images',
  value: 45
}, {
  name: 'Text',
  value: 35
}, {
  name: 'Videos',
  value: 20
}];
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
export function AnalyticsDashboard() {
  return <div className="space-y-6" data-id="element-2">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3">
        <MetricCard icon={<Users data-id="element-5" />} title="Total Reach" value="12,847" subtitle="people" color="blue" data-id="element-4" />
        <MetricCard icon={<TrendingUp data-id="element-7" />} title="Engagement Rate" value="4.2%" subtitle="+0.8% from last week" color="green" data-id="element-6" />
        <MetricCard icon={<MousePointer data-id="element-9" />} title="Click-through Rate" value="2.8%" subtitle="across all platforms" color="yellow" data-id="element-8" />
        <MetricCard icon={<Award data-id="element-11" />} title="Best Platform" value="Facebook" subtitle="+15% this week" color="purple" data-id="element-10" />
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-12">
        {/* Engagement Timeline */}
        <div className="border rounded-lg p-4" data-id="element-13">
          <h3 className="text-md font-medium mb-4" data-id="element-14">
            Daily Engagement Timeline
          </h3>
          <div className="h-64" data-id="element-15">
            <ResponsiveContainer width="100%" height="100%" data-id="element-16">
              <LineChart data={engagementTimelineData} data-id="element-17">
                <CartesianGrid strokeDasharray="3 3" data-id="element-18" />
                <XAxis dataKey="time" data-id="element-19" />
                <YAxis data-id="element-20" />
                <Tooltip data-id="element-21" />
                <Legend data-id="element-22" />
                <Line type="monotone" dataKey="engagement" stroke="#8884d8" activeDot={{
                r: 8
              }} data-id="element-23" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Platform Comparison */}
        <div className="border rounded-lg p-4" data-id="element-24">
          <h3 className="text-md font-medium mb-4" data-id="element-25">Platform Engagement</h3>
          <div className="h-64" data-id="element-26">
            <ResponsiveContainer width="100%" height="100%" data-id="element-27">
              <PieChart data-id="element-28">
                <Pie data={platformComparisonData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                name,
                percent
              }) => `${name}: ${(percent * 100).toFixed(0)}%`} data-id="element-29">
                  {platformComparisonData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-30" />)}
                </Pie>
                <Tooltip data-id="element-31" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Demographics */}
        <div className="border rounded-lg p-4" data-id="element-32">
          <h3 className="text-md font-medium mb-4" data-id="element-33">Audience Demographics</h3>
          <div className="h-64" data-id="element-34">
            <ResponsiveContainer width="100%" height="100%" data-id="element-35">
              <BarChart data={demographicsData} data-id="element-36">
                <CartesianGrid strokeDasharray="3 3" data-id="element-37" />
                <XAxis dataKey="name" data-id="element-38" />
                <YAxis data-id="element-39" />
                <Tooltip data-id="element-40" />
                <Legend data-id="element-41" />
                <Bar dataKey="value" name="Percentage" fill="#82ca9d" data-id="element-42" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-sm text-gray-500 text-center" data-id="element-43">
            55% Female, 45% Male
          </div>
        </div>
        {/* Post Types */}
        <div className="border rounded-lg p-4" data-id="element-44">
          <h3 className="text-md font-medium mb-4" data-id="element-45">
            Top Performing Content Types
          </h3>
          <div className="h-64" data-id="element-46">
            <ResponsiveContainer width="100%" height="100%" data-id="element-47">
              <PieChart data-id="element-48">
                <Pie data={postTypesData} cx="50%" cy="50%" labelLine={false} outerRadius={80} fill="#8884d8" dataKey="value" label={({
                name,
                value
              }) => `${name}: ${value}%`} data-id="element-49">
                  {postTypesData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-50" />)}
                </Pie>
                <Tooltip data-id="element-51" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>;
}
function MetricCard({
  icon,
  title,
  value,
  subtitle,
  color
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600'
  };
  return <div className="border rounded-lg p-4" data-id="element-52">
      <div className="flex items-center gap-3" data-id="element-53">
        <div className={`p-2 rounded-md ${colorClasses[color]}`} data-id="element-54">{icon}</div>
        <div data-id="element-55">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-56">{title}</h3>
          <p className="text-xl font-semibold" data-id="element-57">{value}</p>
          <p className="text-xs text-gray-500" data-id="element-58">{subtitle}</p>
        </div>
      </div>
    </div>;
}